import type { FC } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { PaginationComponentProps } from "react-data-table-component";

const navBtnClass =
  "inline-flex h-8 w-8 items-center justify-center rounded border border-gray-200 bg-white text-gray-700 transition-all duration-200 ease-in-out hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white";

/**
 * Pagination footer aligned with add-in references: "Showing X–Y of Z" and "Page A of B" with chevrons.
 */
export const AddinDataTablePagination: FC<PaginationComponentProps> = ({
  rowsPerPage,
  rowCount,
  currentPage,
  onChangePage,
}) => {
  const totalPages = Math.max(1, Math.ceil(rowCount / rowsPerPage));
  const start = rowCount === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, rowCount);
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;

  return (
    <div className="flex w-full flex-wrap items-center justify-between gap-2 border-t border-gray-200 bg-white px-3 py-2.5">
      <span className="text-sm text-gray-600">
        Showing {rowCount === 0 ? 0 : start}–{end} of {rowCount}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onChangePage(currentPage - 1, rowCount)}
          disabled={isFirstPage}
          aria-label="Previous page"
          className={navBtnClass}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </button>
        <span className="min-w-[7rem] text-center text-sm text-gray-600">
          Page {currentPage} of {totalPages}
        </span>
        <button
          type="button"
          onClick={() => onChangePage(currentPage + 1, rowCount)}
          disabled={isLastPage}
          aria-label="Next page"
          className={navBtnClass}
        >
          <ChevronRight className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
};

AddinDataTablePagination.displayName = "AddinDataTablePagination";
